/*
 * Shared CanvasHistory helpers with optional keyboard shortcuts for undo/redo.
 */
(function initCanvasHistory(globalContext) {
    if (typeof globalContext === 'undefined') {
        return;
    }

    const existingUtils = globalContext.CanvasHistoryUtils;
    if (existingUtils && existingUtils.__initializedKeyboard !== true) {
        existingUtils.bindKeyboardShortcuts(globalContext);
        existingUtils.__initializedKeyboard = true;
        return;
    }
    if (existingUtils) {
        return;
    }

    function createCanvasHistoryManager() {
        return {
            undoStack: [],
            redoStack: [],
            suspendDepth: 0,
            capacity: 200,
            push(entry) {
                if (!entry || typeof entry.undo !== 'function' || typeof entry.redo !== 'function') {
                    return;
                }
                if (this.suspendDepth > 0) {
                    return;
                }
                if (this.undoStack.length >= this.capacity) {
                    this.undoStack.shift();
                }
                this.undoStack.push(entry);
                this.redoStack.length = 0;
            },
            withoutRecording(callback) {
                this.suspendDepth += 1;
                try {
                    if (typeof callback === 'function') {
                        callback();
                    }
                } finally {
                    this.suspendDepth = Math.max(this.suspendDepth - 1, 0);
                }
            },
            undo() {
                const entry = this.undoStack.pop();
                if (!entry) {
                    return false;
                }
                this.redoStack.push(entry);
                this.withoutRecording(() => entry.undo());
                return true;
            },
            redo() {
                const entry = this.redoStack.pop();
                if (!entry) {
                    return false;
                }
                this.undoStack.push(entry);
                this.withoutRecording(() => entry.redo());
                return true;
            },
            clear() {
                this.undoStack.length = 0;
                this.redoStack.length = 0;
            },
        };
    }

    function ensureCanvasHistory(context) {
        const target = context || globalContext;
        if (!target) {
            return null;
        }
        if (target.CanvasHistory && typeof target.CanvasHistory.push === 'function') {
            return target.CanvasHistory;
        }
        const manager = createCanvasHistoryManager();
        target.CanvasHistory = manager;
        return manager;
    }

    function shouldIgnoreKeydownTarget(target) {
        if (!target) {
            return false;
        }
        const nodeName = (target.nodeName || '').toLowerCase();
        if (nodeName === 'input' || nodeName === 'textarea' || nodeName === 'select') {
            return true;
        }
        if (target.isContentEditable) {
            return true;
        }
        return false;
    }

    function bindKeyboardShortcuts(target) {
        const listenerHost = target || globalContext;
        if (!listenerHost || listenerHost.__canvasHistoryKeybound) {
            return;
        }
        const historyRef = ensureCanvasHistory(listenerHost);
        if (!historyRef) {
            return;
        }
        const handler = (event) => {
            if (!event) {
                return;
            }
            if (!event.metaKey && !event.ctrlKey) {
                return;
            }
            if (shouldIgnoreKeydownTarget(event.target)) {
                return;
            }
            const key = (event.key || '').toLowerCase();
            const wantsUndo = key === 'z' && !event.shiftKey;
            const wantsRedo = key === 'y' || (key === 'z' && event.shiftKey);
            let handled = false;
            if (wantsUndo) {
                handled = historyRef.undo();
            } else if (wantsRedo) {
                handled = historyRef.redo();
            }
            if (handled) {
                event.preventDefault();
            }
        };
        listenerHost.addEventListener('keydown', handler, { passive: false });
        listenerHost.__canvasHistoryKeybound = true;
    }

    globalContext.CanvasHistoryUtils = {
        createCanvasHistoryManager,
        ensureCanvasHistory,
        bindKeyboardShortcuts,
        __initializedKeyboard: false,
    };

    ensureCanvasHistory(globalContext);
    bindKeyboardShortcuts(globalContext);
    globalContext.CanvasHistoryUtils.__initializedKeyboard = true;
})(typeof window !== 'undefined' ? window : undefined);
